import classNames from 'classnames';
import type { ReactNode } from 'react';

/* Class strings the design repeats across every band. Kept verbatim (translated to Tailwind 3.4) so a
   section reads like its source markup. */
export const SECTION_CLASS = 'sec relative isolate py-[clamp(3.5rem,_1.75rem_+_6vw,_7.5rem)]';
export const CONTAINER_CLASS = 'mx-auto w-full max-w-[var(--container-wide)] px-[var(--gutter)]';
export const EYEBROW_CLASS = 'eyebrow inline-flex items-center gap-2 font-mono text-[length:var(--fs-2xs)] font-medium uppercase tracking-[var(--ls-eyebrow)] text-[color:var(--gold)]';
export const SECTION_HEAD_CLASS = 'sec__head grid gap-4 lg:grid-cols-[minmax(0,_1.05fr)_minmax(0,_.95fr)] lg:items-end lg:gap-[clamp(2rem,_1rem_+_3vw,_4rem)]';
export const SECTION_HEAD_CENTER_CLASS = 'sec__head sec__head--center mb-[clamp(2rem,_1rem_+_3vw,_3.5rem)] grid justify-items-center gap-x-4 gap-y-0 text-center lg:grid-cols-[minmax(0,_1fr)] lg:items-end lg:gap-x-[clamp(2rem,_1rem_+_3vw,_4rem)]';
export const SECTION_TITLE_CLASS = 'sec__title m-0 mt-[.35rem] text-balance text-[length:var(--fs-3xl)] leading-[var(--lh-snug)] tracking-[var(--ls-heading)] text-[color:var(--fg-strong)] [overflow-wrap:break-word]';
export const GOLD_EM_CLASS = 'not-italic text-[color:var(--gold)] [text-shadow:0_0_24px_rgb(var(--gold-rgb)_/_.35)]';
export const SECTION_LEAD_CLASS = 'sec__lead min-w-0 max-w-[34em] text-[length:var(--fs-lg)] leading-[1.6] text-[color:var(--fg-muted)]';
export const SECTION_LEAD_CENTER_CLASS = 'sec__lead sec__lead--center mx-auto mb-0 mt-4 min-w-0 max-w-[34em] text-[length:var(--fs-lg)] leading-[1.6] text-[color:var(--fg-muted)]';
export const KICKER_CLASS = 'kicker mx-0 mb-[clamp(1.5rem,_1rem_+_2vw,_2.5rem)] mt-[.6rem] max-w-[30em] text-balance text-[length:clamp(1.25rem,_1.02rem_+_.95vw,_1.875rem)] font-semibold leading-[1.3] tracking-[var(--ls-heading)] text-[color:var(--fg-dim)] [overflow-wrap:break-word]';
export const SECONDARY_CTA_CLASS = 'btn btn-primary';

type HaloPosition = 'left' | 'right';

interface HaloProps {
  position: HaloPosition;
  tone?: 'gold' | 'violet';
}

function Halo({ position, tone = 'gold' }: HaloProps) {
  return <span className={classNames('sec__halo absolute aspect-[1] w-[min(70rem,_92%)] rounded-[50%]', tone === 'violet' && 'sec__halo--violet', position === 'right' ? 'sec__halo--r end-[-14%] start-auto top-[-12%]' : 'start-[-12%] top-[-18%]')} />;
}

interface SectionBackgroundProps {
  halos?: readonly HaloProps[];
  children?: ReactNode;
}

/** The rune weave every band opens on, plus its soft halos. Purely decorative. */
export function SectionBackground({ halos = [], children }: SectionBackgroundProps) {
  return (
    <div className='sec__bg pointer-events-none absolute inset-0 z-[-1] overflow-hidden' aria-hidden='true'>
      <span className='sec__weave absolute -inset-px' />
      {halos.map((halo) => (
        <Halo key={`${halo.position}-${halo.tone ?? 'gold'}`} position={halo.position} tone={halo.tone} />
      ))}
      {children}
    </div>
  );
}

interface SimChipProps {
  label: string;
  size?: 'sm' | 'xs';
}

/** "Simulação": marks every illustrative figure on the page. */
export function SimChip({ label, size = 'sm' }: SimChipProps) {
  return (
    <span className={classNames('simchip inline-flex flex-none items-center gap-[.35rem] whitespace-nowrap rounded-full border border-solid border-[color:rgb(var(--gold-rgb)_/_.34)] bg-[color:rgb(var(--gold-rgb)_/_.12)] font-mono text-[length:11px] uppercase leading-none tracking-[.1em] text-[color:var(--gold-hi)]', size === 'xs' ? 'simchip--xs px-[.45rem] py-[.24rem]' : 'px-2 py-[.3rem]')}>
      <i className='size-[5px] flex-none rounded-[50%] bg-[color:var(--gold)] [box-shadow:0_0_8px_rgb(var(--gold-rgb)_/_.8)]' aria-hidden='true' />
      {label}
    </span>
  );
}

interface StarsProps {
  size: 15 | 17;
  label: string;
  className?: string;
  children?: ReactNode;
}

const STAR_PATH = 'm12 2.6 2.9 5.9 6.5.9-4.7 4.6 1.1 6.4-5.8-3-5.8 3 1.1-6.4L2.6 9.4l6.5-.9z';
const STAR_KEYS = ['one', 'two', 'three', 'four', 'five'] as const;

/** Five gold stars read as one image ("Nota 4,9 de 5"). */
export function Stars({ size, label, className, children }: StarsProps) {
  return (
    <span className={classNames('stars inline-flex items-center text-[color:var(--gold)]', className)} role='img' aria-label={label}>
      {STAR_KEYS.map((key) => (
        <svg key={key} width={size} height={size} viewBox='0 0 24 24' fill='currentColor' aria-hidden='true' focusable='false'>
          <path d={STAR_PATH} />
        </svg>
      ))}
      {children}
    </span>
  );
}

/** The arrow the page's primary CTAs end on. */
export function ArrowIcon({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth='1.8' strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <path d='M5 12h14' />
      <path d='m13 6 6 6-6 6' />
    </svg>
  );
}

/** Round check mark used by the form labels and the "ok" notes. */
export function CheckCircleIcon({ className, strokeWidth = '1.8', size }: { className?: string; strokeWidth?: string; size?: number }) {
  return (
    <svg className={className} width={size} height={size} viewBox='0 0 24 24' fill='none' stroke='currentColor' strokeWidth={strokeWidth} strokeLinecap='round' strokeLinejoin='round' aria-hidden='true' focusable='false'>
      <circle cx='12' cy='12' r='9' />
      <path d='m8.4 12.3 2.4 2.4 4.8-5' />
    </svg>
  );
}
